import { useMutation } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ulid } from 'ulid';

import { usePageHeader } from '@/contexts/PageHeaderContext';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { useContractNumber } from '@/hooks/useContractNumber';
import { useGo } from '@/hooks/useResourceNavigation';
import { getMutationOptions } from '@/lib/mutationRefetch';

import { ContractForm } from './contract-form';
import {
	defaultContractFormValues,
	type ContractFormSubmitData,
	type ContractFormValues
} from './contract-input';
import { CREATE_CONTRACT_MUTATION } from './contracts.gql';
import { useCustomerOptions } from './server-data-hooks';

/**
 * Vehicle data structure for cloning
 */
interface CloneVehicleData {
	vehicleId?: string;
	plateNumber?: string | null;
}

/**
 * Complete CloneData interface that includes all contract fields
 * for proper cloning functionality
 */
interface CloneData {
	// Core contract fields
	customerId?: string;
	customerLabel?: string; // Display label for the customer autocomplete
	beginDate?: string;
	endDate?: string;
	amount?: string;
	isAutoRenewed?: boolean;
	simultaneousVehicleLimit?: number | null;
	reservedParkingSlots?: number | null;
	isActive?: boolean;
	preferredLanguage?: string | null;
	includeAllCustomerVehicles?: boolean;
	// Related entities
	vehicles?: CloneVehicleData[];
	vipVehicleIds?: string[];
	// NOTE: cards are not cloned
}

interface LocationState {
	cloneData?: CloneData;
}

const formatDateForInput = (date: Date) => date.toISOString().slice(0, 10);

/**
 * Converts a date string (ISO 8601 or YYYY-MM-DD) to the format expected by HTML date inputs (YYYY-MM-DD)
 */
const normalizeDateForInput = (dateString: string | null | undefined): string | null => {
	if (!dateString) return null;
	// If it's already in YYYY-MM-DD format, return as-is
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
		return dateString;
	}
	// Otherwise, parse and format (handles ISO 8601 dates like 2024-12-14T00:00:00.000Z)
	try {
		const date = new Date(dateString);
		if (Number.isNaN(date.getTime())) return null;
		return date.toISOString().slice(0, 10);
	} catch {
		return null;
	}
};

export const ContractsCreate = () => {
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const go = useGo();
	const location = useLocation();
	const locationState = location.state as LocationState | null;
	const cloneData = locationState?.cloneData;
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		setPageHeader({
			title: cloneData ? 'Clone contract' : 'Create contract',
			description: cloneData
				? 'Create a new contract based on an existing one'
				: 'Set up a new customer contract',
			showBackButton: true,
			onBackClick: () => go({ to: '/contracts' })
		});

		return () => {
			resetPageHeader();
		};
	}, [cloneData, go, resetPageHeader, setPageHeader]);
	const [createContractMutation] = useMutation(CREATE_CONTRACT_MUTATION);
	const idempotentContractId = useMemo(() => ulid(), []);
	const contractNumber = useContractNumber();

	const { customerOptions } = useCustomerOptions();

	const initialValues = useMemo<ContractFormValues>(() => {
		const today = new Date();
		const nextYear = new Date(today);
		nextYear.setFullYear(today.getFullYear() + 1);

		if (cloneData) {
			// Map cloned vehicles to form format (strip contractVehicleId for new creation)
			const clonedVehicles = (cloneData.vehicles ?? []).map(vehicle => ({
				vehicleId: vehicle.vehicleId ?? '',
				plateNumber: vehicle.plateNumber ?? '',
				isDeleted: false
			}));

			// Map cloned VIP vehicles
			const clonedVipVehicles = (cloneData.vipVehicleIds ?? []).map(vehicleId => ({
				vehicleId,
				plateNumber: '',
				isDeleted: false
			}));

			// Normalize dates from ISO 8601 format to YYYY-MM-DD for HTML date inputs
			const normalizedBeginDate = normalizeDateForInput(cloneData.beginDate) ?? formatDateForInput(today);
			const normalizedEndDate = normalizeDateForInput(cloneData.endDate) ?? formatDateForInput(nextYear);

			return {
				...defaultContractFormValues,
				customerId: cloneData.customerId ?? customerOptions[0]?.customerId ?? '',
				beginDate: normalizedBeginDate,
				endDate: normalizedEndDate,
				amount: cloneData.amount ?? '500',
				isAutoRenewed: cloneData.isAutoRenewed ?? false,
				simultaneousVehicleLimit: cloneData.simultaneousVehicleLimit?.toString() ?? '',
				reservedParkingSlots: cloneData.reservedParkingSlots?.toString() ?? '',
				isActive: cloneData.isActive ?? true,
				preferredLanguage: (cloneData.preferredLanguage as 'EN' | 'EL' | '') ?? '',
				includeAllCustomerVehicles: cloneData.includeAllCustomerVehicles ?? true,
				vehicles: clonedVehicles,
				vipVehicles: clonedVipVehicles,
				cards: []
			};
		}

		return {
			...defaultContractFormValues,
			customerId: customerOptions[0]?.customerId ?? '',
			beginDate: formatDateForInput(today),
			endDate: formatDateForInput(nextYear),
			amount: '500',
			vehicles: []
		};
	}, [customerOptions, cloneData]);

	const handleSubmit = async (payload: ContractFormSubmitData) => {
		console.log('Create contract called with payload:', payload);
		setIsSubmitting(true);
		try {
			const mutationInput = {
				contractId: idempotentContractId,
				contractNumber,
				customerId: payload.customerId,
				beginDate: new Date(payload.beginDate).toISOString(),
				endDate: new Date(payload.endDate).toISOString(),
				amount: payload.amount,
				isAutoRenewed: payload.isAutoRenewed,
				simultaneousVehicleLimit: payload.simultaneousVehicleLimit || null,
				reservedParkingSlots: payload.reservedParkingSlots || 0,
				isActive: payload.isActive,
				preferredLanguage: payload.preferredLanguage,
				includeAllCustomerVehicles: payload.includeAllCustomerVehicles,
				vehicleIds: payload.vehicleIds,
				vipVehicleIds: payload.vipVehicleIds,
				cards: payload.cards
			};

			console.log('Full mutation input:', JSON.stringify(mutationInput, null, 2));

			const { data: mutationData } = await createContractMutation(
				getMutationOptions('contracts', {
					input: mutationInput
				})
			);

			console.log('Mutation result:', mutationData);

			if (mutationData?.createContract?.success) {
				toast.success('Contract created successfully.');
				go({
					to: '/contracts',
					state: { selectContractId: idempotentContractId }
				});
			} else {
				const errorMessage =
					mutationData?.createContract?.errorDescription ||
					mutationData?.createContract?.errorCode ||
					'Unknown error occurred';
				toast.error(`Failed to create contract: ${errorMessage}`);
			}
		} catch (error) {
			console.error('Contract creation error:', error);
			toast.error('Failed to create contract. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<FormPageLayout>
			<ContractForm
				mode={cloneData ? 'clone' : 'create'}
				initialData={initialValues}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				customerOptions={customerOptions}
				initialCustomerLabel={cloneData?.customerLabel}
				headerTitle={cloneData ? 'Clone contract' : 'Create contract'}
				contractNumber={contractNumber}
			/>
		</FormPageLayout>
	);
};
